import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Loader2, ChevronRight, Gift } from "lucide-react";
import dayjs from "dayjs";
import { companyConfig } from "./myGiftCardList";
import { listAllGiftCards } from "../../reducers/giftcard.slice";
import { listAllGiftCardRedemption } from "../../reducers/payouts.slice";
import { AuthContext } from "../../context/authContext";
import PageHeader from "../../components/shared/PageHeader";
import GiftCardTile, { retailerTitle, giftCardTypeLabel } from "../../components/member/giftcards/GiftCardTile";
import useGiftCardRedemption from "../../components/member/giftcards/useGiftCardRedemption";
import GiftCardRedemptionDialogs from "../../components/member/giftcards/GiftCardRedemptionDialogs";
import useMediaQuery from "../../components/shared/useMediaQuery";

const PREVIEW_LIMIT = 4;

function SectionLoader() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 140, color: "#c3c8cc" }}>
      <Loader2 size={20} className="tw-animate-spin" style={{ marginRight: 8 }} />
      Loading…
    </div>
  );
}

function EmptyNote({ children }) {
  return (
    <div
      style={{
        height: 120,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px dashed rgba(255,255,255,0.12)",
        borderRadius: 14,
        color: "#c3c8cc",
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

export default function MemberGiftCards() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [transformedData, setTransformedData] = useState({});
  const [storeLoading, setStoreLoading] = useState(true);
  const [storeCardData, setStoreCardData] = useState([]);
  const [myCardsLoading, setMyCardsLoading] = useState(true);

  const { selectedCard, openStoreCard, openCardProcessing, openRejectedCard, imageBase64, openCard, closeStoreCard, closeProcessing, closeRejected } =
    useGiftCardRedemption({ dispatch, showToast, companyConfig });

  useEffect(() => {
    setStoreLoading(true);
    dispatch(listAllGiftCards()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setStoreLoading(false);
        return;
      }

      const filteredGiftCards = Object.entries(res?.payload || {}).reduce((acc, [retailer, cards]) => {
        const activeCards = cards.filter((card) => card.status !== "INACTIVE");
        if (activeCards.length > 0) acc[retailer] = activeCards;
        return acc;
      }, {});

      const vendorsWithSummary = Object.entries(filteredGiftCards).reduce((acc, [retailer, cards]) => {
        const amounts = cards?.map((card) => card?.amount);
        const boosts = cards?.map((card) => card?.boost);

        let hasInStore = false;
        let hasOnline = false;
        let hasBoth = false;
        for (const card of cards) {
          const types = card?.gift_card_type;
          if (types.includes("IN_STORE") && types.includes("ONLINE")) {
            hasBoth = true;
            break;
          }
          if (types.includes("IN_STORE")) hasInStore = true;
          if (types.includes("ONLINE")) hasOnline = true;
        }

        let summarized_type = [];
        if (hasBoth) summarized_type = ["IN_STORE", "ONLINE"];
        else if (hasOnline) summarized_type = ["ONLINE"];
        else if (hasInStore) summarized_type = ["IN_STORE"];

        acc[retailer] = {
          gift_cards: cards,
          min_amount: Math.min(...amounts),
          max_amount: Math.max(...amounts),
          max_boost: Math.max(...boosts),
          gift_card_type: summarized_type,
        };
        return acc;
      }, {});

      setTransformedData(vendorsWithSummary);
      setStoreLoading(false);
    });
  }, []);

  useEffect(() => {
    setMyCardsLoading(true);
    dispatch(listAllGiftCardRedemption()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setMyCardsLoading(false);
        return;
      }
      const formattedGiftCard = res?.payload?.map((item) => ({
        ...item,
        created_at: dayjs(item?.created_at).format("MM/DD/YYYY"),
      }));
      setStoreCardData(formattedGiftCard || []);
      setMyCardsLoading(false);
    });
  }, []);

  const purchasedPillFor = (card) => {
    if (card?.status === "READY_FOR_REDEMPTION") return { text: `Purchased ${card?.created_at}`, tone: "success" };
    if (card?.status === "REJECTED") return { text: "Rejected", tone: "danger" };
    return { text: "Processing", tone: "neutral" };
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 26 : 36, width: "100%", maxWidth: "100%" }}>
      <PageHeader title="Gift Cards" subtitle="Purchase discounted gift cards using your RSC wallet balance." />

      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "flex-end",
            flexDirection: isMobile ? "column" : "row",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: 16.5, fontWeight: 700, color: "#fff", margin: 0 }}>My Gift Cards</h2>
            <p style={{ fontSize: 12.5, color: "#c3c8cc", margin: "4px 0 0" }}>
              Keep track of all your gift card purchases here.
            </p>
          </div>
          {storeCardData?.length >= PREVIEW_LIMIT && (
            <Link
              to="my-gift-cards"
              style={{ display: "flex", alignItems: "center", fontSize: 12.5, fontWeight: 600, color: "#00ADB5", textDecoration: "none" }}
            >
              View All <ChevronRight size={14} style={{ marginLeft: 2 }} />
            </Link>
          )}
        </div>

        {myCardsLoading ? (
          <SectionLoader />
        ) : storeCardData?.length === 0 ? (
          <EmptyNote>You haven't purchased any gift cards yet — browse the store below.</EmptyNote>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 180 : 210}px, 1fr))`, gap: isMobile ? 12 : 16 }}>
            {storeCardData.slice(0, PREVIEW_LIMIT).map((card, index) => {
              const { logo, bg } = companyConfig[card?.gift_card_details?.retailer] || { logo: "/images/amazon.png", bg: "#000000" };
              const pill = purchasedPillFor(card);
              return (
                <GiftCardTile
                  key={card?.id ?? index}
                  logo={logo}
                  bg={bg}
                  retailerName={retailerTitle(card?.gift_card_details?.retailer)}
                  typeText={giftCardTypeLabel(card?.gift_card_details?.gift_card_type)}
                  priceText={`$${card?.actual_amount}`}
                  pillText={pill.text}
                  pillTone={pill.tone}
                  onClick={() => openCard(card)}
                />
              );
            })}
          </div>
        )}
      </div>

      <div>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 16.5, fontWeight: 700, color: "#fff", margin: 0 }}>Gift Cards Store</h2>
          <p style={{ fontSize: 12.5, color: "#c3c8cc", margin: "4px 0 0" }}>
            Find more exciting gift cards and offers below.
          </p>
        </div>

        {storeLoading ? (
          <SectionLoader />
        ) : Object.keys(transformedData).length === 0 ? (
          <EmptyNote>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Gift size={16} /> No gift cards available right now.
            </span>
          </EmptyNote>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 180 : 210}px, 1fr))`, gap: isMobile ? 12 : 16 }}>
            {Object.entries(transformedData).map(([retailer, { gift_cards, min_amount, max_amount, max_boost, gift_card_type }], index) => {
              const { logo, bg } = companyConfig[retailer] || { logo: "/images/amazon.png", bg: "#000000" };
              return (
                <GiftCardTile
                  key={retailer ?? index}
                  logo={logo}
                  bg={bg}
                  retailerName={retailerTitle(retailer)}
                  typeText={giftCardTypeLabel(gift_card_type)}
                  priceText={min_amount === max_amount ? `$${min_amount}` : `$${min_amount} - $${max_amount}`}
                  pillText={`${max_boost}% Boost`}
                  pillTone="primary"
                  onClick={() =>
                    navigate("/dashboard/wallet/gift-cards/my-gift-card", {
                      state: { card: [retailer, { gift_cards, min_amount, max_amount, max_boost, gift_card_type }] },
                    })
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      <GiftCardRedemptionDialogs
        selectedCard={selectedCard}
        openStoreCard={openStoreCard}
        openCardProcessing={openCardProcessing}
        openRejectedCard={openRejectedCard}
        imageBase64={imageBase64}
        onCloseStoreCard={closeStoreCard}
        onCloseProcessing={closeProcessing}
        onCloseRejected={closeRejected}
      />
    </div>
  );
}
